import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../supabase';
import { S, T, playSound } from '../utils/appUtils';
import { STARLINGS } from './HomeScreen';

// ── GOOGLE ONBOARDING ────────────────────────────────────────
export function GoogleOnboarding({user,onComplete}){
  const [name,setName]=useState(user.user_metadata?.full_name?.split(' ')[0]||'');
  const [cls,setCls]=useState(null);
  const [loading,setLoading]=useState(false);

  return(
    <div style={{minHeight:'100vh',background:T.gradHeader,display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <motion.div style={{fontSize:'64px',marginBottom:'8px'}}
        animate={{y:[0,-10,0]}} transition={{repeat:Infinity,duration:2}}>⭐</motion.div>
      <h1 style={{fontSize:'28px',fontWeight:'900',color:'white',margin:'0 0 4px',textAlign:'center'}}>
        Welcome to NeevStar!
      </h1>
      <p style={{fontSize:'14px',color:'rgba(255,255,255,0.85)',margin:'0 0 24px',textAlign:'center',fontWeight:'600'}}>
        Just two quick questions!
      </p>
      <div style={{...S.authCard,width:'100%',maxWidth:'380px'}}>
        <p style={{fontSize:'16px',fontWeight:'900',color:T.dark,margin:'0 0 6px'}}>What is your name?</p>
        <input style={S.inputField} placeholder="Your first name" value={name}
          onChange={e=>setName(e.target.value)}/>
        <p style={{fontSize:'16px',fontWeight:'900',color:T.dark,margin:'12px 0 10px'}}>Which class are you in?</p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'8px',marginBottom:'20px'}}>
          {[3,4,5,6,7].map(c=>(
            <motion.button key={c} whileTap={{scale:0.92}}
              style={{padding:'14px 0',borderRadius:'14px',
                border:cls===c?'3px solid #0066FF':'3px solid #E8E8E8',
                background:cls===c?'#0066FF':'white',fontWeight:'900',fontSize:'18px',cursor:'pointer',
                color:cls===c?'white':'#1A1A2E',fontFamily:"'Nunito',sans-serif"}}
              onClick={()=>setCls(c)}>{c}</motion.button>
          ))}
        </div>
        <motion.button
          style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
            background:'linear-gradient(135deg,#00C851,#00E676)',color:'white',
            fontSize:'16px',fontWeight:'900',cursor:'pointer',marginTop:'4px',
            opacity:name&&cls?1:0.5,fontFamily:"'Nunito',sans-serif"}}
          whileTap={{scale:0.97}}
          onClick={async()=>{
            if(!name||!cls) return;
            setLoading(true);
            await onComplete(name,cls);
          }} disabled={loading||!name||!cls}>
          {loading?"Setting up...":"Let's Start! 🌟"}
        </motion.button>
      </div>
    </div>
  );
}


// ── PASSWORD RESET SCREEN ────────────────────────────────────
export function PasswordResetScreen({onDone}){
  const [password,setPassword]=useState('');
  const [confirm,setConfirm]=useState('');
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [done,setDone]=useState(false);

  const handleReset=async()=>{
    if(password.length<6){setError('Password must be at least 6 characters');return;}
    if(password!==confirm){setError('Passwords do not match');return;}
    setLoading(true);setError('');
    const{error:e}=await supabase.auth.updateUser({password});
    if(e){
      if(e.message.includes('Auth session missing')||e.message.includes('session')){
        setError('Your reset link has expired. Please request a new one.');
      } else {
        setError(e.message);
      }
      setLoading(false);return;
    }
    setDone(true);
    playSound('levelup');
    setTimeout(()=>{
      supabase.auth.signOut();
      onDone();
    },2500);
  };

  return(
    <div style={{minHeight:'100vh',background:'linear-gradient(160deg,#0A0E27 0%,#1A0533 50%,#0D1B4B 100%)',
      display:'flex',flexDirection:'column',padding:'24px',fontFamily:"'Nunito',sans-serif",
      alignItems:'center',justifyContent:'center'}}>
      {done?(
        <motion.div style={{textAlign:'center'}}
          initial={{scale:0}} animate={{scale:1}} transition={{type:'spring'}}>
          <p style={{fontSize:'64px',margin:'0 0 16px'}}>🎉</p>
          <h2 style={{fontSize:'24px',fontWeight:'900',color:'white',margin:'0 0 8px'}}>
            Password Updated!
          </h2>
          <p style={{fontSize:'14px',color:'rgba(255,255,255,0.7)',fontWeight:'700'}}>
            Please log in with your new password
          </p>
        </motion.div>
      ):(
        <motion.div style={{width:'100%',maxWidth:'380px'}}
          initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
          <div style={{textAlign:'center',marginBottom:'28px'}}>
            <p style={{fontSize:'48px',margin:'0 0 8px'}}>🔑</p>
            <h2 style={{fontSize:'24px',fontWeight:'900',color:'white',margin:'0 0 4px'}}>
              Set New Password
            </h2>
            <p style={{fontSize:'13px',color:'rgba(255,255,255,0.6)',fontWeight:'700'}}>
              Choose a strong password for your account
            </p>
          </div>

          {error&&(
            <div style={{background:'rgba(255,45,85,0.2)',borderRadius:'12px',
              padding:'12px 16px',marginBottom:'16px',fontSize:'13px',
              color:'#FF6B8A',fontWeight:'700',border:'1px solid rgba(255,45,85,0.3)'}}>
              {error}
            </div>
          )}

          <p style={{fontSize:'13px',color:'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>
            🔑 New Password
          </p>
          <input
            style={{...S.inputField,background:'rgba(255,255,255,0.1)',
              color:'white',borderColor:'rgba(255,255,255,0.2)',marginBottom:'16px'}}
            type="password" placeholder="Minimum 6 characters"
            value={password} onChange={e=>setPassword(e.target.value)}/>

          <p style={{fontSize:'13px',color:'rgba(255,255,255,0.6)',margin:'0 0 8px',fontWeight:'700'}}>
            🔑 Confirm Password
          </p>
          <input
            style={{...S.inputField,background:'rgba(255,255,255,0.1)',
              color:'white',borderColor:'rgba(255,255,255,0.2)',marginBottom:'24px'}}
            type="password" placeholder="Type password again"
            value={confirm} onChange={e=>setConfirm(e.target.value)}/>

          <motion.button
            style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
              background:'linear-gradient(135deg,#0066FF,#8B2BE2)',color:'white',
              fontSize:'16px',fontWeight:'900',cursor:'pointer',
              fontFamily:"'Nunito',sans-serif",opacity:loading?0.7:1}}
            whileTap={{scale:0.97}} onClick={handleReset} disabled={loading}>
            {loading?'Updating...':'Update Password ✅'}
          </motion.button>

          <button onClick={onDone}
            style={{width:'100%',background:'none',border:'none',
              color:'rgba(255,255,255,0.5)',fontWeight:'700',cursor:'pointer',
              marginTop:'12px',fontSize:'13px',fontFamily:"'Nunito',sans-serif"}}>
            Cancel — Go to Login
          </button>
        </motion.div>
      )}
    </div>
  );
}

// ── ERROR BOUNDARY ────────────────────────────────────────────
export class ScreenErrorBoundary extends React.Component{
  constructor(props){super(props);this.state={error:null,isChunkError:false};}
  static getDerivedStateFromError(err){
    // Detect lazy chunk load failures (network error on code-split import)
    const isChunkError=err?.message?.includes('Loading chunk')||err?.message?.includes('Failed to fetch dynamically imported')||err?.name==='ChunkLoadError';
    return{error:err,isChunkError};
  }
  componentDidCatch(err,info){
    // Log error to console so it's visible in DevTools
    console.error('[NeevStar Error Boundary]', err?.message, err?.stack, info?.componentStack);
    // Auto-reload once on chunk load errors (happens on slow connections after a deploy)
    if(err?.message?.includes('Loading chunk')||err?.name==='ChunkLoadError'){
      const reloaded=sessionStorage.getItem('ns_chunk_reload');
      if(!reloaded){sessionStorage.setItem('ns_chunk_reload','1');window.location.reload();}
    }
  }
  render(){
    if(this.state.error){
      if(this.state.isChunkError){
        return(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
            padding:'60px 32px',textAlign:'center',minHeight:'60vh',fontFamily:"'Nunito',sans-serif"}}>
            <div style={{fontSize:60,marginBottom:16}}>📶</div>
            <h3 style={{fontSize:20,fontWeight:900,color:'#1A1A2E',margin:'0 0 10px'}}>Connection issue</h3>
            <p style={{fontSize:14,color:'#888',fontWeight:700,lineHeight:1.6,margin:'0 0 24px'}}>
              Couldn't load this screen. Check your internet and try again.
            </p>
            <button onClick={()=>window.location.reload()}
              style={{padding:'14px 28px',borderRadius:50,border:'none',
                background:'linear-gradient(135deg,#0066FF,#8B2BE2)',
                color:'white',fontSize:15,fontWeight:900,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
              Reload App 🔄
            </button>
          </div>
        );
      }
      return(
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
          padding:'60px 32px',textAlign:'center',minHeight:'60vh',fontFamily:"'Nunito',sans-serif"}}>
          <div style={{fontSize:60,marginBottom:16}}>😕</div>
          <h3 style={{fontSize:20,fontWeight:900,color:'#1A1A2E',margin:'0 0 10px'}}>Something went wrong</h3>
          <p style={{fontSize:14,color:'#888',fontWeight:700,lineHeight:1.6,margin:'0 0 8px'}}>
            This screen ran into an error. Your XP and progress are safe!
          </p>
          {this.state.error?.message&&(
            <p style={{fontSize:11,color:'#E53935',fontWeight:700,lineHeight:1.5,margin:'0 0 16px',
              background:'#FFF3F3',border:'1px solid #FFCDD2',borderRadius:8,padding:'8px 12px',
              wordBreak:'break-word',textAlign:'left',maxWidth:360}}>
              🔍 {this.state.error.message}
            </p>
          )}
          <button onClick={()=>this.setState({error:null,isChunkError:false})}
            style={{padding:'14px 28px',borderRadius:50,border:'none',
              background:'linear-gradient(135deg,#0066FF,#8B2BE2)',
              color:'white',fontSize:15,fontWeight:900,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── DESKTOP SIDEBAR ──────────────────────────────────────────
export function DesktopSidebar({screen,setScreen,profile,xp,streak,isPremium,onLogout}){
  const level=Math.min(Math.floor(xp/200)+1,10);
  const levelNames=['Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder',
    'Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO 🎯'];
  const nav=[
    {id:'home',      icon:'🏠', label:'Home'},
    {id:'olympiad',  icon:'🏆', label:'Olympiad'},
    {id:'quiz',      icon:'🏆', label:'Quiz World'},
    {id:'multiplayer',icon:'⚔️',label:'Battle Arena'},
    {id:'maths',     icon:'➗', label:'Maths'},
    {id:'science',   icon:'🔬', label:'Science'},
    {id:'language',  icon:'🗣️', label:'Language'},
    {id:'money',     icon:'💰', label:'Money School'},
    {id:'entrepreneurship',icon:'🏪',label:'Biz World'},
    {id:'games',     icon:'🎮', label:'Games Zone'},
    {id:'news',      icon:'📰', label:'News for Kids'},
    {id:'leaderboard',icon:'📊',label:'Leaderboard'},
    {id:'profile',   icon:'👤', label:'Profile'},
  ];
  const xpInLevel=xp%200;
  return(
    <div className="ns-sidebar">
      {/* Logo + user card */}
      <div style={{padding:'20px 18px 16px',borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
        <div style={{marginBottom:12}}>
          <img src="/neevstar_logo.png" alt="NeevStar" style={{width:140,height:'auto',filter:'brightness(0) invert(1)'}}/>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
          <div style={{width:38,height:38,borderRadius:'50%',background:'rgba(255,255,255,0.25)',border:'2px solid rgba(255,255,255,0.5)',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:900,color:'white',flexShrink:0,overflow:'hidden'}}>
            {profile?.avatar_id&&STARLINGS[profile.avatar_id]
              ?<img src={STARLINGS[profile.avatar_id].images.idle} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              :(profile?.child_name||'?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{color:'white',fontWeight:900,fontSize:13,fontFamily:"'Nunito',sans-serif",lineHeight:1.2}}>
              {profile?.child_name||'Student'}
            </div>
            <div style={{color:'rgba(255,255,255,0.55)',fontWeight:700,fontSize:11,fontFamily:"'Nunito',sans-serif"}}>
              Class {profile?.class_level} • {levelNames[level-1]}
            </div>
          </div>
        </div>
        {/* XP bar */}
        <div style={{background:'rgba(255,255,255,0.2)',borderRadius:8,height:8,marginBottom:6,overflow:'hidden'}}>
          <div style={{width:`${(xpInLevel/200)*100}%`,height:'100%',
            background:'#FFD600',borderRadius:8,transition:'width 0.6s'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span style={{color:'#FFD600',fontWeight:900,fontSize:12,fontFamily:"'Nunito',sans-serif"}}>
            ⭐ {xp} XP · Lv{level}
          </span>
          <span style={{color:'white',fontWeight:900,fontSize:12,fontFamily:"'Nunito',sans-serif"}}>
            🔥 {streak} {streak===1?'day':'days'}
          </span>
        </div>
      </div>

      {/* Nav items */}
      <div style={{flex:1,padding:'10px 10px',overflowY:'auto'}}>
        {nav.map(item=>{
          const active=screen===item.id;
          return(
            <button key={item.id} onClick={()=>setScreen(item.id)}
              style={{
                display:'flex',alignItems:'center',gap:10,
                width:'100%',padding:'9px 12px',borderRadius:12,
                border:'none',cursor:'pointer',marginBottom:2,
                background:active?'rgba(255,255,255,0.25)':'rgba(255,255,255,0.08)',
                color:active?'white':'rgba(255,255,255,0.85)',
                fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:13,
                textAlign:'left',transition:'background 0.15s, color 0.15s',
                borderLeft:active?'4px solid #FFD600':'4px solid transparent',
              }}>
              <span style={{fontSize:17,flexShrink:0}}>{item.icon}</span>
              <span style={{flex:1}}>{item.label}</span>
              {active&&<span style={{fontSize:10,color:'#FFD600'}}>●</span>}
            </button>
          );
        })}
      </div>

      {/* Logout — always visible, not buried in Profile */}
      <div style={{padding:'8px 10px 0'}}>
        <button onClick={onLogout} style={{
          display:'flex',alignItems:'center',gap:10,width:'100%',
          padding:'9px 12px',borderRadius:12,border:'1px solid rgba(255,255,255,0.15)',
          cursor:'pointer',background:'rgba(255,255,255,0.05)',color:'rgba(255,255,255,0.65)',
          fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:13,textAlign:'left'}}>
          <span style={{fontSize:17,flexShrink:0}}>🚪</span>
          <span style={{flex:1}}>Log Out</span>
        </button>
      </div>

      {/* Footer */}
      <div style={{padding:'12px 18px',borderTop:'1px solid rgba(255,255,255,0.2)',
        color:'rgba(255,255,255,0.7)',fontSize:10,fontWeight:700,fontFamily:"'Nunito',sans-serif"}}>
        Learn Today. Lead Tomorrow. · Made with ❤️ in India
      </div>
    </div>
  );
}

